// Import custom IssueLabelToken until https://github.com/github/primer/issues/1142 is fixed
import {LabelToken} from '@github-ui/label-token'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {Tooltip} from '@primer/react/next'
import {Link} from '@primer/styled-react'
import {clsx} from 'clsx'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import type {Label$key} from './__generated__/Label.graphql'
import styles from './Label.module.css'

type Props = {
  label: Label$key
  hidden?: boolean
  /**
   * Href getter for the label link
   * @param name Label name
   * @returns href
   */
  getLabelHref: (name: string) => string
  fontWeight?: number
}

const labelQuery = graphql`
  fragment Label on Label {
    id
    nameHTML
    color
    name
    description
  }
`

export function Label({label, hidden = false, getLabelHref, fontWeight}: Props) {
  const {nameHTML, color, id, name, description} = useFragment(labelQuery, label)

  return (
    <Tooltip
      text={description ?? ''}
      type="description"
      aria-label={description ?? ''}
      sx={{
        visibility: description ? 'visible' : 'hidden',
      }}
      className={styles.LabelTooltip}
    >
      {/*
        Remove link from layout and accessibility tree if hidden;
        display:none does not work because of label truncation calculations
      */}
      <Link
        href={getLabelHref(name)}
        className={clsx({'sr-only': hidden})}
        aria-describedby={`${id}-tooltip`}
        sx={{visibility: hidden ? 'hidden' : null}}
      >
        <LabelToken
          interactive
          text={<VerifiedHTMLText html={nameHTML as SafeHTMLString} />}
          fillColor={`#${color}`}
          key={id}
          fontWeight={fontWeight}
        />
        <span className="sr-only" id={`${id}-tooltip`}>
          {description ?? ''}
        </span>
      </Link>
    </Tooltip>
  )
}

try{ Label.displayName ||= 'Label' } catch {}